<template>
  <div
    id="app"
    :class="{
      'in-app': inapp,
      'in-fullpage': inapp && infullpage,
    }">
    <AppGrain />

    <AppHeader />

    <transition @enter="enterNavigationItem" @leave="leaveNavigationItem">
      <PuzzleNavigation
        v-show="!game.playing"
        position="left"
        :gameWon="game.won"
        @click.native="!game.won && slider.changeIndexPuzzle(-1)"
      />
    </transition>

    <main ref="main" :class="{ 'is-playing': game.playing, grabbable: !game.playing && !game.won }">
      <img class="app__image" src="/img/illustrations/logo.png" alt="Yuko Hihuchi x Gucci">

      <transition name="fade">
        <div
          class="show-full-art"
          v-if="game.playing"
          @mouseenter="!isTouchDevice && $root.$emit('show-full-art')"
          @mouseleave="!isTouchDevice && $root.$emit('hide-full-art')"
          @touchstart="isTouchDevice && $root.$emit('show-full-art')"
          @touchend="isTouchDevice && $root.$emit('hide-full-art')"
        >
          <span v-if="!isTouchDevice">{{ $translate('VIEW_COMPLETE') }}</span>
          <span v-else>{{ $translate('HOLD_FOR_COMPLETE') }}</span>
        </div>
      </transition>

      <Puzzle
        class="c-puzzle"
        v-for="(puzzle, key) in puzzles"
        :key="key"
        :ref="key"
        :illustration="puzzle.image"
        :inGame="game.playing"
        :data-color="puzzle.color"
      />

      <p
        class="puzzle__result puzzle__result--mobile"
        v-if="game.won"
      >
        <span v-html="$translate('YOU_WIN')"></span>
      </p>

      <div class="puzzle__tutorial">
        <div class="puzzle__tutorial-container">
          <span v-html="$translate('TUTORIAL')"></span>
          <img src="/img/svg/hand.svg" class="puzzle__tutorial-icon" />
        </div>
      </div>

      <PuzzleButton
        @click.native="click"
        :inGame="game.playing"
        :gameWon="game.won"
      />
    </main>

    <transition @enter="enterNavigationItem" @leave="leaveNavigationItem">
      <PuzzleNavigation
        v-show="!game.playing"
        position="right"
        :gameWon="game.won"
        @click.native="!game.won && slider.changeIndexPuzzle(+1)"
      />
    </transition>

    <AppFooter :muted="muted" />

    <AppCookiebar />

    <RotateDevice />
  </div>
</template>

<script>
import anime from 'animejs';
import AppGrain from '@/components/AppGrain.vue';
import AppHeader from '@/components/AppHeader.vue';
import AppFooter from '@/components/AppFooter.vue';
import Puzzle from '@/components/Puzzle.vue';
import PuzzleButton from '@/components/PuzzleButton.vue';
import PuzzleNavigation from '@/components/PuzzleNavigation.vue';
import RotateDevice from '@/components/RotateDevice.vue';
import AppCookiebar from '@/components/AppCookiebar.vue';

import { animations } from '@/scripts/animations';
import Slider from '@/scripts/Slider';
import { isTouchDevice, inApp, inFullpage } from '@/scripts/utils';
import Loader from './scripts/Loader';
import audio from './scripts/audio';
import eventHub from './scripts/eventHub';

export default {
  name: 'App',

  components: {
    AppGrain,
    AppHeader,
    AppFooter,
    AppCookiebar,
    Puzzle,
    PuzzleButton,
    PuzzleNavigation,
    RotateDevice,
  },

  data() {
    return {
      inapp: inApp(),
      infullpage: inFullpage(),
      slider: null,
      game: {
        playing: false,
        won: false,
      },
      gameAudio: null,
      muted: false,
      isTouchDevice: isTouchDevice(),
      puzzles: {
        'puzzle-0': {
          image: '/img/illustrations/illustration_00.png',
          color: 'beige',
        },
        'puzzle-1': {
          image: '/img/illustrations/illustration_01.png',
          color: 'azure',
        },
        'puzzle-2': {
          image: '/img/illustrations/illustration_02.png',
          color: 'blue',
        },
        'puzzle-3': {
          image: '/img/illustrations/illustration_03.png',
          color: 'pink',
        },
      },
    };
  },

  mounted() {
    const puzzles = [...this.$el.querySelectorAll('.c-puzzle')];
    const illustrations = [];

    Object.keys(this.puzzles).forEach((key) => illustrations.push(this.puzzles[key].image));

    this.slider = new Slider(puzzles, illustrations);
    this.loader = new Loader(this.slider);

    audio.load();

    this.gameAudio = audio.get('game');

    this.$eventHub.$on('game-won', () => {
      this.game.playing = false;
      this.game.won = true;
    });

    eventHub.$on('toggle-mute', () => { this.muted = !this.muted; });
  },

  methods: {
    click() {
      if (this.slider.isInTransition) return;
      if (this.game.playing && !this.game.won) {
        this.close();
        return;
      }
      if (!this.game.playing && this.game.won) {
        this.playNext();
        return;
      }
      this.play();
    },
    play() {
      this.game.playing = true;
      this.game.won = false;
      this.slider.showTutorial();
      this.slider.startGame();
      // Play sounds
      audio.play('play');
      this.fadeInAudio();
    },
    playNext() {
      this.slider.changeIndexPuzzle(+1);
      this.fadeOutAudio();
      this.game.playing = false;
      this.game.won = false;
    },
    close() {
      this.slider.hideTutorial();
      this.slider.addEvents();
      this.slider.resetGame();
      this.game.playing = false;
      this.fadeOutAudio();
      audio.play('close');
    },
    enterNavigationItem(el, done) {
      if (!this.game.won) animations.get('navigationItem')(el).finished.then(() => done());
      else done();
    },
    leaveNavigationItem(el, done) {
      animations.get('navigationItemOut')(el).finished.then(() => done());
    },
    fadeInAudio() {
      this.gameAudio.ref.volume = 0;
      this.gameAudio.ref.play();
      anime({
        targets: this.gameAudio.ref,
        volume: 0.02,
        easing: 'easeInOutCubic',
        duration: 600,
      });
    },
    fadeOutAudio() {
      anime({
        targets: this.gameAudio.ref,
        volume: 0,
        easing: 'easeInOutCubic',
        duration: 600,
        complete: () => this.gameAudio.ref.pause(),
      });
    },
  },
};
</script>

<style lang="scss">
@import './scss/base';
@import './scss/fonts';

html,
body {
  touch-action: none;
}

body.is-loading {
  cursor: wait !important;
}

.fade-enter-active {
  transition: opacity 0.5s $inOutSine 1s,
    transform 0.5s $outBack 1s;
}
.fade-leave-active {
  transition: opacity 0.3s $inOutSine,
    transform 0.5s $outBack;
}
.fade-enter {
  opacity: 0;
  transform: translateY(20px);
}
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

#app {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  max-width: 100vw;
  max-height: 100vh;

  text-align: center;
  background-color: $color-bg;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-flow: row wrap;

  overflow: hidden;

  .app__image {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateX(-50%) translateY(-50%);
  }

  main {
    position: relative;
    text-align: center;
    width: calc(100vw - 50px);
    max-width: 360px;
    transition: box-shadow .6s ease;

    z-index: 5;

    &.is-playing {
      transition: box-shadow .6s ease 0.8s;
      box-shadow: 0 24px 38px 3px rgba(0,0,0,0.14), 0 9px 46px 8px rgba(0,0,0,0.12),
        0 11px 15px -7px rgba(0,0,0,0.20);
    }

    @include aspect-ratio(1, 1);

    @include mq(ipadL) {
      transform: translateY(35px);
    }

    @include mq(d) {
      max-width: 420px;
    }

    @include mq(dm) {
      max-width: 460px;
    }

    @include mq(dl) {
      max-width: 520px;
    }

    @include mq(dxl) {
      max-width: 600px;
    }

    @include mq(mobileX) {
      width: calc(100vw - 40px);
    }
  }

  &.in-app main {
    transform: translateY(-10px);
  }

  &:not(.in-app) main {
    @include mq($and: $is-iphonePlus) {
      transform: translateY(-10px);
    }

    @include mq($and: $is-iphonex) {
      transform: translateY(-15px);
    }

    @include mq($and: $is-iphone11) {
      transform: translateY(-18px);
    }

    @include mq($and: $is-iphoneMax) {
      transform: translateY(-18px);
    }
  }

  .show-full-art {
    position: absolute;
    top: -25px;
    width: 100%;
    text-align: center;
    color: rgba($color-beige, 0.8);
    font-weight: 700;
    font-size: 13px;

    cursor: pointer;

    @include mq($until: mobileX) {
      font-size: 12px;
    }
  }

  .puzzle__result {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba($color: $color-black, $alpha: 0.8);

    font-size: 44px;
    font-weight: 800;
    color: $color-beige;

    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;

    @include mq(ipadL) {
      display: none;
    }
  }

  .puzzle__tutorial {
    position: absolute;
    z-index: 10;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 100%;
    background-color: rgba($color: $color-black, $alpha: 0.8);

    font-size: 14px;
    line-height: 26px;

    display: none;
    align-items: center;
    justify-content: center;

    color: $color-beige;

    &-container {
      padding: 20px 20px 0 20px;

      @include mq(ipadL) {
        padding: 80px 40px 0 40px;
      }
    }

    &-icon {
      margin-top: 40px;
    }
  }

  .Cookie {
    bottom: 40px;
    left: 50%;
    margin-left: -300px;
    width: 600px;

    border-radius: 5px;
    background-color: #3D6389;

    .Cookie__button {
      padding: 18px 40px;
      border-radius: 60px;

      font-weight: 800;
      color: $color-h;
      background-color: $color-white;
    }
  }
}
</style>
